import { useCallback, useEffect, useState } from 'react'

/**
 * IB-282 View 4, AC4.2 (design §2) — a bounded, client-side cursor-walk shared by both 4.2 tiles.
 * Walks at most {@link MAX_PAGES} pages of {@link PAGE_LIMIT} rows each (≤2,000 rows/series/window,
 * the design's own R-30-registered threshold). If the walk stops because the bound was hit (a
 * non-null cursor remains), `overflow=true` is reported — the widget renders a disclosed "window too
 * large to summarize" state, NEVER a silently truncated series that looks complete (the IB-162
 * honesty rule).
 */
const PAGE_LIMIT = 200
const MAX_PAGES = 10

export interface BoundedListPage<T> {
  items: T[]
  nextCursor: string | null
}

export interface BoundedListResult<T> {
  status: 'loading' | 'ok' | 'not_authorized' | 'error'
  rows: T[]
  /** True when the ≤2,000-row/10-page bound was hit before the window was fully walked. */
  overflow: boolean
  message: string | null
  retry: () => void
}

interface InternalState<T> {
  status: BoundedListResult<T>['status']
  rows: T[]
  overflow: boolean
  message: string | null
}

/**
 * @param enabled false means "not authorized" (per the manifest's capability render-gate) — this
 *     hook never issues a fetch when `enabled` is false, so an unauthorized caller never even
 *     attempts the route (the manifest hint IS still independently re-enforced server-side by each
 *     route's own capability guard, defense-in-depth — this is the UX-side half).
 * @param fetchPage fetches ONE cursor page at {@link PAGE_LIMIT} rows; `cursor=null` means "first page".
 * @param depsKey a stable string key (e.g. `JSON.stringify(filter)`) — re-fetches when it changes.
 */
export function useBoundedList<T>(
  enabled: boolean,
  fetchPage: (cursor: string | null, signal: AbortSignal) => Promise<BoundedListPage<T>>,
  depsKey: string,
): BoundedListResult<T> {
  const [state, setState] = useState<InternalState<T>>({
    status: enabled ? 'loading' : 'not_authorized',
    rows: [],
    overflow: false,
    message: null,
  })

  const fetchNow = useCallback((): (() => void) => {
    if (!enabled) {
      setState({ status: 'not_authorized', rows: [], overflow: false, message: null })
      return () => {}
    }
    const controller = new AbortController()
    setState({ status: 'loading', rows: [], overflow: false, message: null })
    void (async () => {
      try {
        let cursor: string | null = null
        let rows: T[] = []
        let overflow = false
        for (let page = 0; page < MAX_PAGES; page += 1) {
          const result = await fetchPage(cursor, controller.signal)
          rows = rows.concat(result.items)
          cursor = result.nextCursor
          if (cursor === null) break
          if (page === MAX_PAGES - 1) overflow = true
        }
        if (controller.signal.aborted) return
        setState({ status: 'ok', rows, overflow, message: null })
      } catch {
        if (controller.signal.aborted) return
        setState({
          status: 'error',
          rows: [],
          overflow: false,
          message: 'Could not load this tile. The rest of the dashboard is unaffected.',
        })
      }
    })()
    return () => controller.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, depsKey])

  useEffect(() => {
    const abort = fetchNow()
    return abort
  }, [fetchNow])

  return { status: state.status, rows: state.rows, overflow: state.overflow, message: state.message, retry: fetchNow }
}

export { PAGE_LIMIT }
